import { Link } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import api from '../../api/axios';
import { getPublicSetting } from '../../utils/publicSettings';
import { getRecaptchaToken } from '../../utils/recaptcha';
import toast from 'react-hot-toast';

const InstagramIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
    <circle cx="12" cy="12" r="4"/>
    <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor"/>
  </svg>
);

const PinterestIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12c0 4.24 2.64 7.86 6.36 9.32-.09-.79-.17-2.01.04-2.88.19-.79 1.23-5.03 1.23-5.03s-.31-.63-.31-1.55c0-1.45.84-2.53 1.89-2.53.89 0 1.32.67 1.32 1.47 0 .9-.57 2.24-.87 3.48-.25 1.04.52 1.89 1.54 1.89 1.85 0 3.09-2.38 3.09-5.19 0-2.14-1.44-3.74-4.06-3.74-2.96 0-4.8 2.21-4.8 4.68 0 .85.25 1.45.64 1.91.18.21.2.3.14.55-.05.19-.16.63-.21.81-.07.26-.28.35-.51.25-1.43-.58-2.09-2.14-2.09-3.9 0-2.9 2.45-6.38 7.3-6.38 3.9 0 6.47 2.82 6.47 5.85 0 4-2.24 7-5.58 7-1.12 0-2.17-.6-2.53-1.28l-.7 2.68c-.21.79-.63 1.58-1 2.21.75.22 1.55.34 2.38.34 5.52 0 10-4.48 10-10S17.52 2 12 2z" />
  </svg>
);

const WhatsAppIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.47 14.38c-.29-.15-1.72-.85-1.99-.95-.27-.1-.46-.15-.66.15-.19.29-.75.95-.92 1.14-.17.2-.34.22-.63.07-.29-.15-1.22-.45-2.32-1.43-.86-.76-1.44-1.71-1.6-2-.17-.29-.02-.45.13-.6.13-.13.29-.34.44-.51.15-.17.19-.29.29-.48.1-.2.05-.37-.02-.51-.07-.15-.66-1.59-.9-2.18-.24-.57-.48-.5-.66-.51-.17-.01-.37-.01-.56-.01-.2 0-.51.07-.78.37-.27.29-1.02 1-1.02 2.44s1.05 2.83 1.19 3.03c.15.19 2.06 3.15 5 4.41.7.3 1.24.48 1.67.62.7.22 1.34.19 1.84.12.56-.08 1.72-.7 1.97-1.38.24-.68.24-1.26.17-1.38-.07-.12-.26-.19-.55-.34z"/>
    <path d="M12.01 2C6.5 2 2 6.5 2 12.01c0 1.79.47 3.53 1.36 5.06L2 22l5.06-1.33a10 10 0 0 0 4.95 1.31h.01c5.51 0 10-4.5 10-10.01C22.02 6.5 17.52 2 12.01 2zm0 18.15h-.01c-1.55 0-3.06-.41-4.38-1.2l-.31-.19-3.26.85.87-3.18-.2-.32a8.15 8.15 0 0 1-1.25-4.3c0-4.51 3.67-8.18 8.18-8.18 4.51 0 8.18 3.67 8.18 8.18 0 4.51-3.67 8.34-8.32 8.34z"/>
  </svg>
);

const Footer: React.FC = () => {
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState(''); // honeypot — real users leave this blank
  const [subscribing, setSubscribing] = useState(false);
  const [social, setSocial] = useState({
    instagram: 'https://www.instagram.com/shoplanresavage?igsh=MXBpdDFoNnFkbWh6dw%3D%3D&utm_source=qr',
    pinterest: 'https://pin.it/45300tffQ',
    whatsapp: 'https://wa.me/2349160353402',
  });

  useEffect(() => {
    const fetchSocial = async () => {
      const [instagram, pinterest, whatsapp] = await Promise.all([
        getPublicSetting('social_instagram', social.instagram),
        getPublicSetting('social_pinterest', social.pinterest),
        getPublicSetting('social_whatsapp', social.whatsapp),
      ]);
      setSocial({ instagram, pinterest, whatsapp });
    };
    fetchSocial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    try {
      setSubscribing(true);
      const recaptcha_token = await getRecaptchaToken('newsletter_subscribe');
      await api.post('/api/newsletter/subscribe', { email, website, recaptcha_token });
      toast.success('Thank you for subscribing');
      setEmail('');
    } catch {
      toast.error('Subscription failed. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  return (
   <footer className="bg-primary text-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">

          {/* Brand */}
          <div className="md:col-span-1">
            <img
  src="/logo-white.png"
  alt="Lanre Savage"
  className="h-20 w-auto mb-4"
/>
            <p className="text-xs text-nude-light leading-relaxed tracking-wide">
              Luxury clothing crafted with intention. Timeless pieces for the modern individual.
            </p>
            <div className="flex space-x-4 mt-6">
              <a href={social.instagram} target="_blank" rel="noreferrer" aria-label="Instagram"
                className="text-nude-light hover:text-white transition-colors">
                <InstagramIcon />
              </a>
              <a href={social.pinterest} target="_blank" rel="noreferrer" aria-label="Pinterest"
                className="text-nude-light hover:text-white transition-colors">
                <PinterestIcon />
              </a>
              <a href={social.whatsapp} target="_blank" rel="noreferrer" aria-label="WhatsApp"
                className="text-nude-light hover:text-white transition-colors">
                <WhatsAppIcon />
              </a>
            </div>
          </div>

          {/* Shop */}
          <div>
            <h3 className="text-xs tracking-widest uppercase mb-6 text-nude-light">Shop</h3>
            <ul className="space-y-3">
              {[
                { label: 'New Arrivals', href: '/shop?sort=newest' },
                { label: 'Collections', href: '/collections' },
                { label: 'Men', href: '/shop?category=men' },
                { label: 'Women', href: '/shop?category=women' },
                { label: 'Sale', href: '/shop?sale=true' },
              ].map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-xs text-nude-light hover:text-white transition-colors tracking-wide"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-xs tracking-widest uppercase mb-6 text-nude-light">Company</h3>
            <ul className="space-y-3">
              {[
                { label: 'About Us', href: '/about' },
                { label: 'FAQ', href: '/faq' },
                { label: 'Contact', href: '/contact' },
              ].map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-xs text-nude-light hover:text-white transition-colors tracking-wide"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-xs tracking-widest uppercase mb-6 text-nude-light">Newsletter</h3>
            <p className="text-xs text-nude-light leading-relaxed mb-4 tracking-wide">
              Subscribe for exclusive access to new collections and private events.
            </p>
            <form onSubmit={handleSubscribe}>
              {/* Honeypot field — invisible to real users, bots often fill every input */}
              <input
                type="text"
                name="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
                aria-hidden="true"
              />
              <div className="flex flex-col space-y-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="YOUR EMAIL"
                  className="bg-transparent border-b border-nude-light text-white text-xs tracking-widest py-2 outline-none placeholder-nude-light focus:border-white transition-colors"
                />
                <button
                  type="submit"
                  disabled={subscribing}
                  className="text-xs tracking-widest uppercase text-primary bg-white py-2 hover:bg-nude transition-colors disabled:opacity-50"
                >
                  {subscribing ? 'Subscribing...' : 'Subscribe'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-secondary mt-12 pt-8 flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
          <p className="text-xs text-nude-light tracking-wide">
            © {new Date().getFullYear()} Lanre Savage. All rights reserved.
          </p>
          <div className="flex space-x-6">
            {[
              { label: 'Privacy Policy', href: '/privacy' },
              { label: 'Terms of Service', href: '/terms' },
              { label: 'Shipping Policy', href: '/shipping-policy' },
              { label: 'Returns', href: '/returns' },
            ].map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className="text-xs text-nude-light hover:text-white transition-colors tracking-wide"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;